import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CheckCheck } from 'lucide-angular';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent, IconSource, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { DynamicComponentRenderer } from 'src/app/@shared/directives/dynamic-component-renderer.directive';
import { MarkdownRenderPipe } from 'src/app/@shared/pipes/markdown-render.pipe';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { CREDITS_SVG, GENERAL_AGENT_SVG } from 'src/app/common/svgs';
import { LLM_PROVIDERS, MESSAGE_TYPE, MessageItem, THREAD_METADATA_STATUS } from '../../../../models/model';
import { AGENT_MODE, MESSAGE_FEEDBACK, ChatAttachment, QueryOptions } from '../../../../models/types';
import { IModelInfo } from '../../../../services/general-agent.service';
import { DOCUMENT_MODEL } from 'src/app/contract-management/models/contract-management.model';
import { SidePanelService } from '../../../../services/side-panel.service';
import { ThreadMessagesService } from '../../../../services/thread-messages.service';
import { AttachmentService } from 'src/app/contract-management/services/attachment.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { isSummarizationMessage } from '../../../../utils/utils';
import { AttachmentGroupComponent } from '../../../../../../@shared/components/attachment-group/attachment-group.component';

@Component({
    selector: 'app-ai-message',
    standalone: true,
    imports: [
        CommonModule,
        ButtonComponent,
        MarkdownRenderPipe,
        DynamicComponentRenderer,
        TooltipModule,
        AttachmentGroupComponent,
        SanitizeHtmlPipe,
        MenuComponent,
        IconComponent,
    ],
    templateUrl: './ai-message.component.html',
    styleUrl: './ai-message.component.scss'
})
export class AiMessageComponent {
    // Inputs
    message = input.required<MessageItem>();
    isOwner = input<boolean>(false);
    isPublicView = input<boolean>(false);
    hasFeedback = input<{ like: boolean, dislike: boolean }>({ like: false, dislike: false });
    modelDetails = input<{ [key in LLM_PROVIDERS]: IModelInfo }>({} as any);

    // Services
    sidePanelService = inject(SidePanelService);
    threadMessagesService = inject(ThreadMessagesService);
    attachmentService = inject(AttachmentService);
    toastService = inject(ToastService);

    // Outputs
    copy = output<string>();
    feedback = output<MESSAGE_FEEDBACK>();

    // State
    isCopied = signal<boolean>(false);
    MESSAGE_FEEDBACK = MESSAGE_FEEDBACK;

    /**
     * True while this message's content can still grow, passed to MarkdownRenderPipe
     * so it knows whether an unclosed ```mermaid fence is still being written or was
     * abandoned. Without it a diagram the model never closed — because the user hit
     * stop, the output hit its token limit, or the stream errored — shows a
     * "Generating diagram…" spinner that never resolves, on every later reload too.
     *
     * is_final_response settles this message on its own; the thread status covers the
     * intermediate messages of a turn, which never carry that flag, and resolves them
     * as soon as the thread stops for any reason.
     */
    isStreaming = computed<boolean>(() => {
        if (this.message().additional_kwargs?.is_final_response) return false;
        const status = this.threadMessagesService.threadMetadataStatus();
        return status === THREAD_METADATA_STATUS.PENDING || status === THREAD_METADATA_STATUS.IN_PROGRESS;
    });


    modeDetailsByMessageId = computed<Map<string, { icon: string, source: IconSource, label: string }>>(() => {
        const messages = this.threadMessagesService.messages();
        const map = new Map<string, { icon: string, source: IconSource, label: string }>();
        let currentModeDetails: { icon: string, source: IconSource, label: string } | null = null;

        for (const msg of messages) {
            if (msg.type === MESSAGE_TYPE.HUMAN) {
                const mode = msg['additional_kwargs']?.['mode'];
                currentModeDetails = mode === AGENT_MODE.AGENTIC
                    ? { icon: CREDITS_SVG.AGENT_MODE, source: IconSourceEnum.SVG, label: 'Agentic mode' }
                    : mode === AGENT_MODE.ASK
                        ? { icon: 'zap', source: IconSourceEnum.LUCIDE, label: 'Ask mode' }
                        : null;
            } else if (currentModeDetails) {
                map.set(msg.id, currentModeDetails);
            }
        }

        return map;
    });

    // Icons
    CheckCheck = CheckCheck;
    onCopy() {
        // Use the smart copy that includes previous AI messages and cleans metadata
        this.copyAiMessageContent(this.message());
    }

    onFeedback(type: MESSAGE_FEEDBACK) {
        this.feedback.emit(type);
    }

    onAttachmentOpen(attachment: any) {
        const attachmentId = attachment._id;
        if (!attachmentId) {
            this.toastService.error('Cannot open file: missing attachment id');
            return;
        }
        this.attachmentService.getAttachmentWithSignedUrl(attachmentId).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.sidePanelService.open('FILE_PREVIEW', { file: res.data, documentId: res.data._id });
                } else {
                    this.toastService.error('Failed to load file preview');
                }
            },
            error: (err) => {
                this.toastService.error('Failed to load file preview: ' + (err?.message ?? 'Unknown error'));
            }
        });
    }

    /**
     * Files shared via `present_files` tool results in this AI message's response cycle,
     * as ChatAttachment[]. Read from the precomputed per-message map so it is not
     * re-derived on every change detection pass.
     */
    presentedFileAttachments = computed<ChatAttachment[]>(() => {
        const presentedFiles = this.threadMessagesService.presentedFilesByMessageId().get(this.message().id) ?? [];

        return presentedFiles.map((file: any) => ({
            _id: file._id || file.attachmentId || file.filename,
            docModel: DOCUMENT_MODEL.ATTACHMENT,
            name: file.filename || file.filePath?.split('/').pop() || 'file',
            type: file.type,
            group: file.group,
            attachmentData: {
                _id: file._id || file.attachmentId,
                filename: file.filename,
                fullUrl: file.s3Url,
                type: file.type,
            } as any
        } as ChatAttachment));
    });

    getModelProviderDetails(message: MessageItem) {
        const provider = message['provider'] || message['response_metadata']?.['model_provider'];
        if (!provider) return { icon: '', model: '' };

        const modelInfo = this.modelDetails();

        if (provider.toLowerCase().includes('openai')) {
            return { icon: GENERAL_AGENT_SVG.OPENAI, model: modelInfo[LLM_PROVIDERS.OPENAI]?.model || provider };
        }
        if (provider.toLowerCase().includes('anthropic')) {
            return { icon: GENERAL_AGENT_SVG.ANTHROPIC, model: modelInfo[LLM_PROVIDERS.ANTHROPIC]?.model || provider };
        }
        if (provider.toLowerCase().includes('google')) {
            return { icon: GENERAL_AGENT_SVG.GOOGLE, model: modelInfo[LLM_PROVIDERS.GOOGLE]?.model || provider };
        }
        if (provider.toLowerCase().includes('xai')) {
            return { icon: GENERAL_AGENT_SVG.GROK, model: modelInfo[LLM_PROVIDERS.XAI]?.model || provider };
        }
        // Fallback or default
        return { icon: GENERAL_AGENT_SVG.OPENAI, model: provider };
    }

    /**
     * Returns display details for the Ask / Agentic mode badge shown in the
     * final-response toolbar. The mode value ("ASK" | "AGENTIC") is stamped onto
     * additional_kwargs by FeedbackMiddleware (Python) and flows through the
     * RabbitMQ → socket path unchanged.
     *
     * Returns null for older messages that pre-date the mode stamp so the badge
     * is simply hidden rather than showing a broken/empty chip.
     */
    getAgentModeDetails(message: MessageItem): { label: string; icon: string; iconSource: 'lucide' | 'svg' } | null {
        const mode = message.additional_kwargs?.['mode'];
        if (mode === AGENT_MODE.ASK) {
            // Zap icon mirrors the Ask mode option in the thread-input selector
            return { label: 'Ask', icon: 'zap', iconSource: 'lucide' };
        }
        if (mode === AGENT_MODE.AGENTIC) {
            // Star/sparkle svg mirrors the Agentic mode option in the thread-input selector
            return { label: 'Agent', icon: CREDITS_SVG.AGENT_MODE, iconSource: 'svg' };
        }
        // No mode on message (pre-feature messages) — render nothing
        return null;
    }

    private removeMetadataJsonBlocks(content: string): string {
        if (!content) return content;

        // Pattern to match markdown code blocks (```json ... ``` or ``` ... ```)
        // that contain evidence_sources or follow_ups
        const markdownJsonBlockPattern = /```(?:json)?\s*\n?([\s\S]*?)```/g;

        let cleanedContent = content.replace(markdownJsonBlockPattern, (match, codeContent) => {
            // Check if the code block contains evidence_sources or follow_ups
            const trimmedContent = codeContent.trim();
            if (trimmedContent.includes('"evidence_sources"') || trimmedContent.includes('"follow_ups"') ||
                trimmedContent.includes("'evidence_sources'") || trimmedContent.includes("'follow_ups'")) {
                // Try to parse as JSON to confirm it's a JSON block
                try {
                    const parsed = JSON.parse(trimmedContent);
                    if (parsed.evidence_sources || parsed.follow_ups) {
                        return ''; // Remove this block
                    }
                } catch {
                    // If it's not valid JSON but contains the keys, still remove it
                    if (trimmedContent.includes('evidence_sources') || trimmedContent.includes('follow_ups')) {
                        return '';
                    }
                }
            }
            return match; // Keep the block if it doesn't contain metadata
        });

        // Also handle plain JSON objects in the text (not in code blocks)
        // Pattern to match JSON objects that might be inline
        const jsonObjectPattern = /\{[\s\S]*?"(?:evidence_sources|follow_ups)"[\s\S]*?\}/g;
        cleanedContent = cleanedContent.replace(jsonObjectPattern, (match) => {
            try {
                const parsed = JSON.parse(match);
                if (parsed.evidence_sources || parsed.follow_ups) {
                    return ''; // Remove this JSON object
                }
            } catch {
                // If parsing fails, check if it contains the keys as strings
                if (match.includes('"evidence_sources"') || match.includes('"follow_ups"') ||
                    match.includes("'evidence_sources'") || match.includes("'follow_ups'")) {
                    return '';
                }
            }
            return match;
        });

        // Clean up multiple consecutive newlines that might result from removals
        cleanedContent = cleanedContent.replace(/\n{3,}/g, '\n\n').trim();

        return cleanedContent;
    }

    copyAiMessageContent(message: MessageItem) {
        //Starting from this message, go backwards, until a human message is found,
        //From that human message, go forwards, until this message, copy the content of all the ai messages content in between
        const messages = this.threadMessagesService.messages();
        const currentIndex = messages.findIndex(msg => msg.id === message.id);

        if (currentIndex === -1) {
            console.error('Message not found in messages array');
            return;
        }

        // Go backwards from current message to find the first human message
        let humanMessageIndex = -1;
        for (let i = currentIndex; i >= 0; i--) {
            if (messages[i].type === MESSAGE_TYPE.HUMAN) {
                humanMessageIndex = i;
                break;
            }
        }

        // Collect all AI messages from the human message (exclusive) to the current message (inclusive)
        // Skip summarization messages as they are internal context management
        const aiMessages: MessageItem[] = [];
        for (let i = humanMessageIndex + 1; i <= currentIndex; i++) {
            if (messages[i].type === MESSAGE_TYPE.AI && messages[i].content && !isSummarizationMessage(messages[i])) {
                aiMessages.push(messages[i]);
            }
        }

        // Join all AI message contents with newlines and clean metadata JSON blocks
        const combinedContent = aiMessages
            .map(msg => this.removeMetadataJsonBlocks(msg.content))
            .join('\n\n');

        navigator.clipboard.writeText(combinedContent).then(() => {
            this.isCopied.set(true);
            setTimeout(() => {
                this.isCopied.set(false);
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy messages:', err);
        });
    }

    getToolParamsForAiMessage(message: MessageItem): CustomMenuItem[] | null {
        const messages = this.threadMessagesService.messages();
        const currentIndex = messages.findIndex(msg => msg.id === message.id);

        if (currentIndex === -1) {
            return null;
        }

        for (let i = currentIndex - 1; i >= 0; i--) {
            if (messages[i].type === MESSAGE_TYPE.HUMAN) {
                const toolParams = messages[i]['additional_kwargs']?.['toolParams'] || null;
                if (toolParams && Array.isArray(toolParams)) {
                    const menuItems: CustomMenuItem[] = [];

                    toolParams.forEach(param => {
                        const key = Object.keys(param)[0];
                        const values = param[key];

                        switch (key) {
                            case QueryOptions.PROJECTS:
                                values.forEach((project: any) => {
                                    if (project?._id && project?.name) {
                                        menuItems.push({
                                            label: project.name,
                                            value: project._id,
                                            icon: GENERAL_AGENT_SVG.PROJECT,
                                            iconSource: IconSourceEnum.SVG,
                                            callback: () => { }
                                        });
                                    }
                                });
                                break;

                            case QueryOptions.KNOWLEDGE_BASE:
                                if (Array.isArray(values)) {
                                    values.forEach((kb: any) => {
                                        if (kb?._id && kb?.name) {
                                            menuItems.push({
                                                label: kb.name,
                                                value: kb._id,
                                                icon: GENERAL_AGENT_SVG.KNOWLEDGE_BASE,
                                                iconSource: IconSourceEnum.SVG,
                                                callback: () => { }
                                            });
                                        }
                                    });
                                } else if (values && typeof values === 'object') {
                                    (values.stores || []).forEach((store: any) => {
                                        if (store?._id && store?.name) {
                                            menuItems.push({
                                                label: store.name,
                                                value: store._id,
                                                icon: GENERAL_AGENT_SVG.KNOWLEDGE_BASE,
                                                iconSource: IconSourceEnum.SVG,
                                                callback: () => { }
                                            });
                                        }
                                    });
                                    (values.files || []).forEach((file: any) => {
                                        if (file?._id && file?.name) {
                                            menuItems.push({
                                                label: file.name,
                                                value: file._id,
                                                icon: GENERAL_AGENT_SVG.KNOWLEDGE_BASE,
                                                iconSource: IconSourceEnum.SVG,
                                                callback: () => { }
                                            });
                                        }
                                    });
                                }
                                break;
                            case QueryOptions.FORM_QUESTIONS:
                                (typeof values === 'string' ? JSON.parse(values) : (values || [])).forEach((formQuestion: any) => {
                                    if (formQuestion?._id && formQuestion?.name) {
                                        menuItems.push({
                                            label: formQuestion.name,
                                            value: formQuestion._id,
                                            icon: 'circle-help',
                                            iconSource: IconSourceEnum.LUCIDE,
                                            callback: () => { }
                                        });
                                    }
                                });
                                break;
                            case QueryOptions.KPIS:
                                (typeof values === 'string' ? JSON.parse(values) : (values || [])).forEach((kpi: any) => {
                                    if (kpi?._id && kpi?.name) {
                                        menuItems.push({
                                            label: kpi.name,
                                            value: kpi._id,
                                            icon: GENERAL_AGENT_SVG.KNOWLEDGE_BASE,
                                            iconSource: IconSourceEnum.SVG,
                                            callback: () => { }
                                        });
                                    }
                                });
                                break;

                            case QueryOptions.LINKED_DOCS:
                                (Array.isArray(values) ? values : []).forEach((doc: any) => {
                                    menuItems.push({
                                        label: doc?.name || 'Linked Document',
                                        value: doc?._id || doc,
                                        icon: GENERAL_AGENT_SVG.PROJECT,
                                        iconSource: IconSourceEnum.SVG,
                                        callback: () => { }
                                    });
                                });
                                break;
                            case QueryOptions.USE_STYLES:
                                values.forEach((style: any) => {
                                    menuItems.push({
                                        label: `Style: ${style}`,
                                        value: style,
                                        icon: GENERAL_AGENT_SVG.USE_STYLE,
                                        iconSource: IconSourceEnum.SVG,
                                        callback: () => { }
                                    });
                                });
                                break;
                            case QueryOptions.WEB_SEARCH:
                                menuItems.push({
                                    label: 'Web search',
                                    value: QueryOptions.WEB_SEARCH,
                                    icon: 'globe',
                                    iconSource: IconSourceEnum.LUCIDE,
                                    callback: () => { }
                                });
                                break;
                            case QueryOptions.CANVAS:
                                menuItems.push({
                                    label: 'Canvas',
                                    value: QueryOptions.CANVAS,
                                    icon: GENERAL_AGENT_SVG.CANVAS,
                                    iconSource: IconSourceEnum.SVG,
                                    callback: () => { }
                                });
                                break;
                            case QueryOptions.CUSTOM_INSTRUCTIONS:
                                menuItems.push({
                                    label: 'Custom instructions',
                                    value: QueryOptions.CUSTOM_INSTRUCTIONS,
                                    icon: GENERAL_AGENT_SVG.INSTRUCTIONS,
                                    iconSource: IconSourceEnum.SVG,
                                    callback: () => { }
                                });
                                break;
                            case QueryOptions.IMAGE_ANALYSIS:
                                menuItems.push({
                                    label: 'Image analysis',
                                    value: QueryOptions.IMAGE_ANALYSIS,
                                    icon: 'image-plus',
                                    iconSource: IconSourceEnum.LUCIDE,
                                    callback: () => { }
                                });
                                break;
                        }
                    });
                    return menuItems.length > 0 ? menuItems : null;
                }
            }
        }
        return null;
    }
}
